import { createSlice } from "@reduxjs/toolkit";
import { searchEngineApi } from "Services/SearchEngineService";
import { searchEngineCAApi } from "Services/SearchEngineServiceCA";
import { projectFundersApi } from "Services/ProjectFundersService";
import { getInitialState } from "./getInitialState";

const searchBar = {
    any: [],
    all: [],
    none: [],
    noneFoundation: [],
    directorName: [],
    foundationName: [],
    yearList: [],
    enableCrossReferenceSearch: false,
    crossReferenceSearchTerms: [],
    stemmingSearch: false,
    thesaurusSearch: false,
    tags: [],
    showFundersOnce: true,
    refreshTags: "",
    showFundersWithoutDonations: false,
};

const searchFilters = {
    fromCountry: ["United States"],
    toCountry: [],
    provinceOrState: [],
    county: [],
    city: [],
    sector: [],
    subSector: [],
    givingInt: 0,
    minGrantsToState: 0,
    years: [],
    grantSize: [0, 11],
    assertSize: [0, 14],
    medianSize: [0, 11],
    foundation: ["Private Funders"],
};

const additionalFilters = {
    hasOnlineApplication: false,
    hasApplicationDescription: false,
    hasAdditionalInformation: false,
    doesNotHaveWebsite: false,
    doesHaveWebsite: false,
    hasPhoneNumber: false,
    showDesignedGiven: false,
    showAdvisedFunds: false,
    showCharitableOrg: true,
    showOtherFoundations: false,
    hasEmail: false,
};

const mainInitialState = {
    searchBar,
    searchFilters,
    additionalFilters,
    page: {
        orderDirection: "desc",
        orderField: "amountofgifts",
        pageNumber: 0,
        pageSize: 50,
    },
};

const byKeywordsInitialState = {
    ...mainInitialState,
    page: {
        ...mainInitialState.page,
        orderField: "averageDonationsYear",
    },
};

const byNameInitialState = {
    ...mainInitialState,
    page: {
        ...mainInitialState.page,
        orderDirection: "asc",
        orderField: "doneename",
    },
};

const byDirectorInitialState = {
    ...mainInitialState,
    page: {
        ...mainInitialState.page,
        orderDirection: "asc",
        orderField: "doneename",
    },
};

const byYearInitialState = {
    ...mainInitialState,
    page: {
        ...mainInitialState.page,
        orderDirection: "asc",
        orderField: "doneename",
    },
};

const byCustomInitialState = {
    searchBar: {
        customDatabaseId: "",
        contactName: [],
        organizationName: [],
    },
    searchFilters: {
        provinceOrStateList: [],
        cityList: [],
        foundation: ["Private Funders"],
    },
    page: {
        orderDirection: "asc",
        orderField: "organizationName",
        pageNumber: 0,
        pageSize: 50,
    },
};

// Giving History gets its own searchFilters shape (rather than reusing mainInitialState's
// directly) so the Peer Search EIN filter (doneeEins) doesn't leak into other search types that
// share mainInitialState (e.g. international-funding) — it should never be a required field, and
// defaults to an empty/no-op array so it never affects a normal (non-Peer) Giving History search.
const givingHistoryInitialState = {
    ...mainInitialState,
    searchFilters: { ...searchFilters, doneeEins: [] },
};

export const initialState = {
    "giving-history": givingHistoryInitialState,
    keywords: byKeywordsInitialState,
    name: byNameInitialState,
    director: byDirectorInitialState,
    year: byYearInitialState,
    "international-funding": mainInitialState,
    custom: byCustomInitialState,
    selectedFunders: [],
    searchResultFilters: {
        searchBar,
        searchFilters,
        additionalFilters,
    },
    byCustomSearchResultsFilters: {
        searchBar: byCustomInitialState.searchBar,
        searchFilters: byCustomInitialState.searchFilters,
    },
    aggrFilters: null,
    tab: "giving-history",
    showResult: false,
    showEmptyResult: false,
    clearSelection: "",
    resultKey: 0,
};

// Utility function to pick only allowed keys from an object
const pickAllowed = (target, allowedKeys) =>
    Object.fromEntries(
        Object.entries(target || {}).filter(([key]) => key in allowedKeys)
    );

export const searchEngineSlice = createSlice({
    name: "searchEngineV3",
    initialState: getInitialState(),
    reducers: {
        setState: (state, action) => {
            Object.assign(state, action.payload);
        },
        loadFilters: (state, action) => {
            const { searchBar, searchFilters, additionalFilters } =
                action.payload;

            const defaults = getInitialState()[state.tab];
            const currentTab = state[state.tab];

            if (currentTab) {
                const searchBarTemp = { ...searchBar };
                const searchFiltersTemp = { ...searchFilters };

                // Remove the title property from each crossReferenceSearchTerm if it exists
                if (searchBarTemp.crossReferenceSearchTerms) {
                    searchBarTemp.crossReferenceSearchTerms = searchBarTemp.crossReferenceSearchTerms.map((term) => {
                        const termTemp = { ...term };
                        if (termTemp.title) {
                            delete termTemp.title;
                        }
                        if(termTemp.valueListField) {
                            delete termTemp.valueListField;
                        }
                        return termTemp;
                    });
                }

                // Ensure that the upper bounds of grantSize, assertSize, and medianSize do not exceed their maximum values
                searchFiltersTemp.grantSize = [
                    searchFiltersTemp.grantSize?.[0] || 0,
                    Math.min(searchFiltersTemp.grantSize?.[1] || 11, 11),
                ];
                searchFiltersTemp.assertSize = [
                    searchFiltersTemp.assertSize?.[0] || 0,
                    Math.min(searchFiltersTemp.assertSize?.[1] || 14, 14),
                ];
                searchFiltersTemp.medianSize = [
                    searchFiltersTemp.medianSize?.[0] || 0,
                    Math.min(searchFiltersTemp.medianSize?.[1] || 11, 11),
                ];

                // Ensure that the foundation filter is set to the default if it is not provided
                searchFiltersTemp.foundation =
                    searchFiltersTemp.foundation && searchFiltersTemp.foundation.length > 0
                        ? searchFilters.foundation
                        : defaults.searchFilters.foundation;

                //Set the searchBar, searchFilters, and additionalFilters for the current tab, merging the defaults with the provided values
                currentTab.searchBar = {
                    ...defaults.searchBar,
                    ...pickAllowed(currentTab.searchBar, defaults.searchBar),
                    ...pickAllowed(searchBarTemp, defaults.searchBar),
                };

                currentTab.searchFilters = {
                    ...defaults.searchFilters,
                    ...pickAllowed(
                        currentTab.searchFilters,
                        defaults.searchFilters
                    ),
                    ...pickAllowed(searchFiltersTemp, defaults.searchFilters),
                };

                currentTab.additionalFilters = {
                    ...defaults.additionalFilters,
                    ...pickAllowed(
                        currentTab.additionalFilters,
                        defaults.additionalFilters
                    ),
                    ...pickAllowed(
                        additionalFilters,
                        defaults.additionalFilters
                    ),
                };
            }
        },
        loadRegionalFilters: (state, action) => {
            const searchFilters = action.payload ?? {};

            const defaults = getInitialState()[state.tab];
            const currentTab = state[state.tab];

            if (currentTab) {
                currentTab.searchFilters = {
                    ...defaults.searchFilters,
                    ...pickAllowed(currentTab.searchFilters, defaults.searchFilters),
                    ...pickAllowed(searchFilters, defaults.searchFilters),
                };
            }
        },
        loadByCustomFilters: (state, action) => {
            const { searchBar, searchFilters } = action.payload;

            state.custom.searchBar = {
                customDatabaseId: searchBar.customDatabaseId || "",
                contactName: searchBar.contactName || [],
                organizationName: searchBar.organizationName || [],
            };
            state.custom.searchFilters = {
                provinceOrStateList: searchFilters.provinceOrStateList || [],
                cityList: searchFilters.cityList || [],
            };
        },
        loadRegionalByCustomFilters: (state, action) => {
            const searchFilters = action.payload ?? {};

            state.custom.searchFilters = {
                provinceOrStateList: searchFilters.provinceOrStateList || [],
                cityList: searchFilters.cityList || [],
            };
        },
        setSearchbar: (state, action) => {
            if (state[state.tab].searchBar) {
                state[state.tab].searchBar[action.payload.fieldName] =
                    action.payload.value;
            }
        },
        setFilter: (state, action) => {
            if (state[state.tab].searchFilters) {
                state[state.tab].searchFilters[action.payload.fieldName] =
                    action.payload.value;
            }
        },
        setPage: (state, action) => {
            const { fieldName, value } = action.payload;
            state[state.tab].page[fieldName] = value;
        },
        setAdditionalFilters: (state, action) => {
            if (state[state.tab].additionalFilters) {
                state[state.tab].additionalFilters[action.payload.fieldName] =
                    action.payload.value;
            }
        },
        toggleCrossReference: (state, action) => {
            state[state.tab].searchBar.enableCrossReferenceSearch =
                action.payload;
        },
        addCrossReferenceSearchTerm: (state, action) => {
            state[state.tab].searchBar.crossReferenceSearchTerms.push(
                action.payload
            );
        },
        removeCrossReferenceSearchTerm: (state, action) => {
            const { id } = action.payload;
            state[state.tab].searchBar.crossReferenceSearchTerms = state[
                state.tab
            ].searchBar.crossReferenceSearchTerms.filter(
                (term) => term.id !== id
            );
        },
        updateCrossReferenceSearchTermValue: (state, action) => {
            const { id, valueList } = action.payload;
            const termIndex = state[
                state.tab
            ].searchBar.crossReferenceSearchTerms.findIndex(
                (term) => term.id === id
            );
            if (termIndex !== -1) {
                state[state.tab].searchBar.crossReferenceSearchTerms[
                    termIndex
                ].valueList = valueList;
            }
        },
        addGeoFiltersToCrossRef: (state, action) => {
            const { id, provinceOrState, county, city } = action.payload;

            if (provinceOrState.length > 0) {
                const existingStateTermIndex = state[state.tab].searchBar.crossReferenceSearchTerms.findIndex(term => term.type === "state");
                const existingCountyTermIndex = state[state.tab].searchBar.crossReferenceSearchTerms.findIndex(term => term.type === "county");
                const existingCityTermIndex = state[state.tab].searchBar.crossReferenceSearchTerms.findIndex(term => term.type === "city");

                if (existingStateTermIndex !== -1) {
                    state[state.tab].searchBar.crossReferenceSearchTerms[existingStateTermIndex].valueList = provinceOrState;
                }
                else {
                    state[state.tab].searchBar.crossReferenceSearchTerms.push({
                        id,
                        type: "state",
                        valueList: provinceOrState,
                    });
                }

                if (existingCountyTermIndex !== -1) {
                    state[state.tab].searchBar.crossReferenceSearchTerms[existingCountyTermIndex].valueList = provinceOrState;
                }
                else {
                    state[state.tab].searchBar.crossReferenceSearchTerms.push({
                        id: id + "-county",
                        type: "county",
                        valueList: county,
                    });
                }

                if (existingCityTermIndex !== -1) {
                    state[state.tab].searchBar.crossReferenceSearchTerms[existingCityTermIndex].valueList = provinceOrState;
                }
                else {
                    state[state.tab].searchBar.crossReferenceSearchTerms.push({
                        id: id + "-city",
                        type: "city",
                        valueList: city,
                    });
                }
            }
        },
        setSelectedFunders: (state, action) => {
            state.selectedFunders = action.payload;
        },
        setClearSelection: (state, action) => {
            state.clearSelection = action.payload;
        },
        setTab: (state, action) => {
            state.tab = action.payload;

            if (state.tab !== action.payload) {
                state.aggrFilters = null;
            }
        },
        setFilterOpen: (state, action) => {
            state.filterOpen = action.payload;
        },
        setShowResult: (state, action) => {
            state.showResult = action.payload;
        },
        bumpResultKey: (state) => {
            state.resultKey += 1;
        },
        clearFilters: (state) => {
            const _initialState = getInitialState();
            state[state.tab].searchBar = _initialState[state.tab].searchBar;
            state[state.tab].searchFilters =
                _initialState[state.tab].searchFilters;
            state[state.tab].additionalFilters =
                _initialState[state.tab].additionalFilters;
            state[state.tab].page = _initialState[state.tab].page;
            state.byCustomFilters = _initialState.byCustomFilters;
        },
        resetSearchResultFilters: (state) => {
            state.searchResultFilters = getInitialState().searchResultFilters;
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                projectFundersApi.endpoints.getAllProjectFundersProfileId
                    .matchFulfilled,
                (state, action) => {
                    state.projectFundersProfileIdList = action.payload;
                }
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByGivingHistoryV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByGivingHistoryCAV2
                    .matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByKeywordV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByNameV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByNameCAV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByDirectorV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByDirectorCAV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByYear.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByYearCA.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByInternacionalFundingV2
                    .matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByCustomV2.matchPending,
                handleApiSearchPending
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByGivingHistoryV2
                    .matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByGivingHistoryCAV2
                    .matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByKeywordV2.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByNameV2.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByNameCAV2.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByDirectorV2.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByDirectorCAV2.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByYear.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineCAApi.endpoints.searchByYearCA.matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByInternacionalFundingV2
                    .matchFulfilled,
                handleApiSearchFulfilled
            )
            .addMatcher(
                searchEngineApi.endpoints.searchByCustomV2.matchFulfilled,
                handleApiSearchByCustomFulfilled
            );
    },
});

const handleApiSearchPending = (state, action) => {
    state.showEmptyResult = false;
};

const handleApiSearchFulfilled = (state, action) => {
    state[state.tab].page.pageNumber = action.payload.pageNumber;
    state[state.tab].page.pageSize = action.payload.pageSize;
    state[state.tab].page.orderDirection = action.payload.orderDirection;
    state[state.tab].page.orderField = action.payload.orderField;

    state.searchResultFilters.searchBar = state[state.tab].searchBar;
    state.searchResultFilters.searchFilters = state[state.tab].searchFilters;
    state.searchResultFilters.additionalFilters =
        state[state.tab].additionalFilters;

    state.aggrFilters = action.payload.aggrFilters;
    state.showEmptyResult = action.payload.items.length === 0;
};

const handleApiSearchByCustomFulfilled = (state, action) => {
    state[state.tab].page.pageNumber = action.payload.pageNumber;
    state[state.tab].page.pageSize = action.payload.pageSize;
    state[state.tab].page.orderDirection = action.payload.orderDirection;
    state[state.tab].page.orderField = action.payload.orderField;

    state.byCustomSearchResultsFilters.searchBar = state.custom.searchBar;
    state.byCustomSearchResultsFilters.searchFilters =
        state.custom.searchFilters;
    state.showEmptyResult = action.payload.items.length === 0;
};

export const selectSearchEngineState = (state) => state.searchEngineStateV3;
export const selectTab = (state) => selectSearchEngineState(state).tab;

export const selectActiveSearchBar = (state) =>
    selectSearchEngineState(state)[selectTab(state)].searchBar;
export const selectActiveSearchFilters = (state) =>
    selectSearchEngineState(state)[selectTab(state)].searchFilters;
export const selectActiveAdditionalFilters = (state) =>
    selectSearchEngineState(state)[selectTab(state)].additionalFilters;

export const {
    setState,
    setPage,
    loadFilters,
    loadRegionalFilters,
    loadByCustomFilters,
    loadRegionalByCustomFilters,
    setSearchbar,
    setFilter,
    setAdditionalFilters,
    toggleCrossReference,
    addCrossReferenceSearchTerm,
    removeCrossReferenceSearchTerm,
    updateCrossReferenceSearchTermValue,
    setSelectedFunders,
    setClearSelection,
    setTab,
    setFilterOpen,
    setShowResult,
    bumpResultKey,
    clearFilters,
    addGeoFiltersToCrossRef,
    resetSearchResultFilters,
} = searchEngineSlice.actions;

export default searchEngineSlice.reducer;
